import type { AudioStore } from './audio';
import type { ErrorData } from '../types/error-data';
import type { GameData } from '../types/game-data';
import type { GameSettings } from '../types/game-settings';
import type { TLayout } from '../types/layout';
import type { SignUpData } from '../types/sign-up';
import type { QuizSocket } from '../types/socket';
import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx';
import React from 'react';
import io from 'socket.io-client';

import { QuestionStore } from './question';
import { ResultsStore } from './results';
import { TeamStore } from './team';
import { ENV, TELEGRAM_SUPPORT_LINK } from '../constants';
import { i18n } from '../configs/i18n';
import { publishError } from '../configs/sentry/sentry-utils';
import { ApiService } from '../services/api';
import { parseApiError } from '../services/api-error';
import { AppStates } from '../types/app-states';
import { layoutFromTheme } from '../types/layout';
import { getDefaultLanguage } from '../utils/language';
import { getJsonStorageItem, setStorageItem } from '../utils/storage';
import { updateUrl } from '../utils/update-url';



export class AppStore {
  state = AppStates.Initial;
  fetching = false;
  userName: string | null = null;
  userId: string | null = null;
  gameData: GameData | undefined;
  error: ErrorData = null;
  token: string | null = null;
  locale: string = getDefaultLanguage();
  headerHeight = 0;
  gameSettings?: GameSettings;
  hideVideo: boolean;
  isSocketConnected = false;
  isGameEnded = false;
  isInitialized = false;
  layout: TLayout = 'quizheroes';

  disposers: IReactionDisposer[] = [];

  private isActiveTabRegistered = false;
  private registeredActiveTabStorageKey: string | null = null;

  teamStore: TeamStore;

  api!: ApiService;
  socket?: QuizSocket;
  questionStore!: QuestionStore;
  resultsStore!: ResultsStore;
  audioStore?: AudioStore;

  get isAuthorized() {
    return this.state === AppStates.SignedIn;
  }

  get videoSrc() {
    return this.gameData?.stream;
  }

  get automaticVideoConfig() {
    return this.gameData?.automatic_video;
  }

  get canChangeCaptain() {
    return Boolean(this.gameData?.can_change_captain);
  }

  get isCaptain() {
    return this.teamStore?.isCaptain;
  }

  get isSocialsVisible() {
    return this.gameSettings?.footer_socials ?? ENV.FOOTER_SOCIALS;
  }

  get isFaqAvailable() {
    return this.gameSettings?.faq ?? true;
  }

  get isGameWithBreak() {
    return this.gameSettings?.has_break ?? true;
  }

  get isAudioChatAvailable() {
    return this.isSocketConnected && (this.gameSettings?.audio_chat ?? false);
  }

  get gameId() {
    return this.gameData?.id;
  }

  get telegramSupportLink() {
    return this.gameSettings?.telegram_support_link ?? TELEGRAM_SUPPORT_LINK;
  }

  get prioritizeMyTeam() {
    return this.gameSettings?.prioritize_my_team ?? false;
  }

  get savedSessionStorageKey() {
    return this.gameId ? `quiz-heroes-last-user-${this.gameId}` : undefined;
  }

  get savedSession() {
    try {
      return this.getSavedSession();
    } catch (error) {
      return null;
    }
  }

  get activeTabStorageKey() {
    if (!this.gameData?.user_token) {
      return '';
    }
    return `quizheroes-active-tabs-counter-${this.gameData?.user_token}`;
  }

  constructor(config: {
    token: string | null;
    hideVideo: boolean;
    name: string | null;
  }) {
    const { token, hideVideo, name } = config;
    this.setToken(token);
    if (name) {
      this.setUserName(name);
    }
    this.hideVideo = hideVideo;
    this.teamStore = new TeamStore(this);
    makeAutoObservable(
      this,
      {
        api: false,
        socket: false,
        teamStore: false,
        questionStore: false,
        resultsStore: false,
        audioStore: false,
      },
      { autoBind: true },
    );
  }

  configureAPI() {
    this.layout = layoutFromTheme(ENV.THEME);
    this.api = new ApiService(ENV.API_URL);
  }

  init = async () => {
    try {
      this.configureAPI();

      this.resultsStore = new ResultsStore(this);
      this.questionStore = new QuestionStore(this);
    } catch (error) {
      publishError(error as Error);
      this.setError({
        reason_key: 'error.config',
      });
      return;
    }
    this.configureSocket();
    this.listenSocket();

    if (!this.isInitialized) {
      this.start();
      runInAction(() => {
        this.isInitialized = true;
      });
    } else if (this.state === AppStates.SignedIn) {
      this.registerActiveTab();
    }

    this.disposers.forEach((disposer) => {
      disposer();
    });
    this.disposers = [];

    this.disposers.push(
      reaction(
        () => this.state,
        (state) => {
          if (state === AppStates.SignedIn) {
            this.saveSession();
            this.configureSocket();
            this.listenSocket();
          }

          if (state === AppStates.Initial) {
            this.configureSocket();
            this.listenSocket();
          }
        },
      ),
    );

    this.disposers.push(
      reaction(
        () => this.isCaptain,
        (isCaptain) => {
          const nextToken = this.getTokenByRole(isCaptain);

          if (nextToken && this.token !== nextToken) {
            this.setToken(nextToken);

            updateUrl(nextToken);
          }
        },
      ),
    );

    this.disposers.push(
      reaction(
        () => this.locale,
        (locale) => {
          if (locale) {
            i18n.changeLanguage(locale);
          }
        },
      ),
    );
  };

  getTokenByRole(isCaptain: boolean) {
    return this.gameData?.[isCaptain ? 'captain_link' : 'team_link'].split('token=')[1];
  }

  setToken(token: string | null) {
    this.token = token;
  }

  start = async () => {
    try {
      this.setFetching(true);
      const isChecked = await this.check();

      if (isChecked && this.userName) {
        await this.connect(this.userName, this.savedSession?.userToken);
      }
    } catch (error) {
      const apiError = parseApiError(error);
      this.setError({ reason: apiError.message });
    } finally {
      this.setFetching(false);
    }
  };

  check = async () => {
    const token = this.token;

    if (!token) {
      this.setError({
        reason_key: 'error.token',
      });
      return false;
    }

    try {
      const { data } = await this.api.check(token);

      if (data.locale) {
        this.setLocale(data.locale);
      }

      this.setGameData({
        name: data.name,
        id: data.id,
        settings: data.settings,
      } as GameData);
      this.setGameSettings(data.settings);
      this.setState(AppStates.Checked);
      return true;
    } catch (error) {
      const apiError = parseApiError(error);

      if (apiError.status === 409) {
        this.socket?.once('gameStarted', this.start);
        if (apiError.header) {
          this.setGameData({ name: apiError.header } as GameData);
        }
      }

      this.setError({
        reason: apiError.message,
        header: apiError.header,
      });
      return false;
    }
  };

  setState(value: AppStates) {
    this.state = value;
  }

  setCheked() {
    this.state = AppStates.Checked;
  }

  setFetching(value: boolean) {
    this.fetching = value;
  }

  setUserName(value: string) {
    this.userName = value;
  }

  setLocale(value: string) {
    this.locale = value;
  }

  setError(error?: Partial<ErrorData>) {
    const { header, header_key, reason, reason_key, withoutHeader } = error || {};
    this.state = AppStates.Error;

    this.error = {
      header,
      header_key,
      reason,
      reason_key,
      withoutHeader,
    };
  }

  connect(name: string, userToken?: string) {
    if (!this.token) {
      throw new Error('Token is required.');
    }

    this.setUserName(name);
    this.setFetching(true);
    return this.api
      .connect(this.token, name, userToken)
      .then((response) => {
        this.setGameData(response.data);
        this.registerActiveTab();
        this.setState(AppStates.SignedIn);

        this.teamStore.setScore(
          this.gameData?.team_stat || {
            score: 0,
            rounds: [],
          },
        );

        return response.data;
      })
      .catch((error) => {
        const apiError = parseApiError(error);
        publishError(error);
        this.setError({
          header: apiError.header,
          reason: apiError.message,
          reason_key: apiError.header ? undefined : 'error.server',
        });
      })
      .finally(() => {
        this.setFetching(false);
      });
  }

  configureSocket() {
    this.socket?.close();
    this.socket = undefined;

    if (!this.token) {
      return;
    }

    this.socket = io({
      query: {
        token: this.token,
        team_room: String(this.gameData?.team_room ?? ''),
        user_token: String(this.gameData?.user_token ?? ''),
      },
    });
  }

  listenSocket() {
    const socket = this.socket;
    if (!socket) {
      return;
    }
    socket.on('connect', () => {
      this.setIsSocketConnected(true);
    });
    socket.on('disconnect', () => {
      this.setIsSocketConnected(false);
    });
    socket.on('gameEnded', () => {
      this.setIsGameEnded(true);
    });
  }

  cleanUp() {
    this.unregisterActiveTab();
    this.socket?.close();
    this.disposers.forEach((disposer) => {
      disposer();
    });
    this.disposers = [];
  }

  getSavedSession() {
    if (!this.savedSessionStorageKey) {
      throw new Error('Cannot get saved session because of empty storage key.');
    }

    return getJsonStorageItem<{ userName: string; userToken: string }>(this.savedSessionStorageKey);
  }

  saveSession() {
    if (!this.savedSessionStorageKey) {
      throw new Error('Cannot save session because of empty storage key.');
    }

    setStorageItem(
      this.savedSessionStorageKey,
      JSON.stringify({
        userName: this.userName || '',
        userToken: this.gameData?.user_token || '',
      }),
    );
  }

  restoreSession = () => {
    const savedSession = this.getSavedSession();
    if (!savedSession) {
      throw new Error('Cannot restore session because of empty saved session.');
    }
    if (!savedSession.userName || !savedSession.userToken) {
      throw new Error('Cannot restore session because of empty userName or userToken.');
    }
    this.connect(savedSession.userName, savedSession.userToken);
  };

  // TODO: move to team or stay here?
  // captain
  makeCaptain = (userToken: string) => {
    if (!this.token) {
      throw new Error('Token is required.');
    }

    this.setFetching(true);
    this.api
      .makeCaptain(this.token, userToken)
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        this.setFetching(false);
      });
  };

  signUp = async (value: SignUpData): Promise<string> => {
    try {
      const { data } = await this.api.signUp(value);
      return data.url;
    } catch (error) {
      throw parseApiError(error);
    }
  };

  public endGame = () => {
    this.unregisterActiveTab();
    this.cleanGameData();
    this.start();
  };

  registerActiveTab() {
    const storageKey = this.activeTabStorageKey;

    if (!storageKey || this.isActiveTabRegistered) {
      return;
    }

    this.registeredActiveTabStorageKey = storageKey;
    this.isActiveTabRegistered = true;
    this.increaseActiveWindows(storageKey);
    this.trackActiveWindow();
  }

  unregisterActiveTab() {
    if (!this.isActiveTabRegistered) {
      return;
    }

    const storageKey = this.registeredActiveTabStorageKey;

    this.isActiveTabRegistered = false;
    this.registeredActiveTabStorageKey = null;
    this.stopTrackActiveWindow();

    if (storageKey) {
      this.decreaseActiveWindows(storageKey);
    }
  }

  trackActiveWindow = () => {
    window.addEventListener('storage', this.handleActiveTabs);
    window.addEventListener('pagehide', this.handleTabClose);
  };

  stopTrackActiveWindow = () => {
    window.removeEventListener('storage', this.handleActiveTabs);
    window.removeEventListener('pagehide', this.handleTabClose);
  };

  handleActiveTabs = (event: StorageEvent) => {
    if (event.key === this.activeTabStorageKey && Number(event.newValue || 0) >= 2) {
      this.endGame();
    }
  };

  handleTabClose = (event: PageTransitionEvent) => {
    if (event.persisted) {
      return;
    }

    this.unregisterActiveTab();
  };

  increaseActiveWindows(storageKey: string) {
    const count = Number(localStorage.getItem(storageKey) || 0);
    localStorage.setItem(storageKey, String(count + 1));
  }

  decreaseActiveWindows(storageKey: string) {
    const count = Number(localStorage.getItem(storageKey) || 0);
    const nextCount = Math.max(0, count - 1);

    if (nextCount === 0) {
      localStorage.removeItem(storageKey);
    } else {
      localStorage.setItem(storageKey, String(nextCount));
    }
  }

  cleanGameData() {
    this.userId = null;
    this.userName = null;
    this.gameData = undefined;
    this.gameSettings = undefined;
    this.state = AppStates.Initial;
    this.isGameEnded = false;
  }

  setHeaderHeight(value: number) {
    this.headerHeight = value;
  }

  setGameSettings = (value?: GameSettings) => {
    this.gameSettings = value;
  };

  setGameData = (value?: GameData) => {
    this.gameData = value;

    if (value?.settings) {
      this.setGameSettings(value.settings);
    }
  };

  setIsGameEnded = (value: boolean) => {
    this.isGameEnded = value;
  };

  setIsSocketConnected = (value: boolean) => {
    this.isSocketConnected = value;
  };

  setAudioStore = (store: AudioStore) => {
    this.audioStore = store;
  };
}

export const AppStoreContext = React.createContext<AppStore | null>(null);

export const useAppStore = () => {
  const appStore = React.useContext(AppStoreContext);

  if (!appStore) {
    throw new Error('useAppStore must be used within a AppStoreContext.Provider.');
  }

  return appStore;
};
